import { DataTableV2, Highlight, Text, Title } from "components/commons";
import React, { useCallback, useMemo, useState } from "react";
import locale from "localization";
import styles from "../shift-records-details-cash-and-non-cash.module.scss";
import { initialFilterState } from "modules/shift-records/shift-records-filter.state";
import useDataTable from "hooks/useDataTable";
import { isArray } from "lodash";
import { PaymentMethod } from "enums";
import { useModal } from "hooks";
import PaymentMethodTransactionModal from "../shift-details-non-cash-summary/payment-method-transaction-modal/payment-method-transaction-modal";
import ReminderHexIcon from "../shift-details-non-cash-summary/reminder-hex-icon";
import { keyAccountSummaryColumn } from "./shift-details-key-account-summary-column";
import {
  getKeyAccountGroupKey,
  getKeyAccountPayments,
  groupKeyAccountPayments,
  keyAccountSummaryMapper,
} from "./shift-details-key-account-summary.mapper";

export default function ShiftDetailsKeyAccountSummary({
  shift,
  refetchShift,
  pageMode,
  updatePaymentMethodRefNumber,
  updateTransactionPaymentMethod,
  updateTransactionAccountability,
  employees,
}) {
  const keyAccountTransactionModal = useModal();
  const [selectedKeyAccount, setSelectedKeyAccount] = useState(null);
  const [hasReferenceNumberChange, setHasReferenceNumberChange] = useState(false);

  const transactionsItemDetails = useMemo(() => {
    const result = [];
    Object.values(shift.transactionItem).forEach((productTransaction) => {
      if (isArray(productTransaction)) {
        result.push(...productTransaction);
      }
    });
    return result;
  }, [shift.transactionItem]);

  const keyAccountPayments = useMemo(() => getKeyAccountPayments(shift), [shift]);

  const summary = useMemo(() => groupKeyAccountPayments(keyAccountPayments), [keyAccountPayments]);

  const keyAccountTransactions = useMemo(
    () =>
      keyAccountPayments
        .filter((t) => getKeyAccountGroupKey(t) === selectedKeyAccount)
        .sort((a, b) =>
          String(b.transactionNumber ?? "").localeCompare(
            String(a.transactionNumber ?? ""),
            undefined,
            { numeric: true },
          ),
        ),
    [keyAccountPayments, selectedKeyAccount],
  );

  const handleUpdatePaymentMethodRefNumber = useCallback(
    (transactionPaymentMethodId, newRefNumber) => {
      setHasReferenceNumberChange(true);
      updatePaymentMethodRefNumber?.(transactionPaymentMethodId, newRefNumber);
    },
    [updatePaymentMethodRefNumber],
  );

  const handleUpdateTransactionPaymentMethod = useCallback(
    (transactionPaymentMethodId, changes) => {
      setHasReferenceNumberChange(true);
      updateTransactionPaymentMethod?.(transactionPaymentMethodId, changes);
    },
    [updateTransactionPaymentMethod],
  );

  const onViewKeyAccountTransaction = useCallback(
    (key) => {
      keyAccountTransactionModal.show({});
      setSelectedKeyAccount(key);
    },
    [keyAccountTransactionModal],
  );

  const { table } = useDataTable({
    value: summary,
    filter: { initialState: initialFilterState(), mapper: (data) => data },
    table: {
      key: "keyAccountSummary",
      name: "keyAccountSummary",
      mapper: (val) => keyAccountSummaryMapper({ val, onViewKeyAccountTransaction }),
      columns: keyAccountSummaryColumn,
    },
  });

  return (
    <>
      <Title small black>
        {locale.keyAccountSummary}
      </Title>
      <div className={styles.spacer10} />
      {hasReferenceNumberChange && (
        <>
          <Highlight
            withIcon
            warning
            icon={<ReminderHexIcon />}
            className={styles.reminderHighlight}
            style={{
              opacity: 1,
              transform: "none",
              alignItems: "flex-start",
              paddingTop: 20,
              paddingBottom: 20,
              gap: 12,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Text
                color="var(--color-cheddar)"
                style={{ fontSize: 14, fontWeight: 900, lineHeight: "24px" }}
              >
                {locale.reminder}
              </Text>
              <Text color="var(--color-night)" style={{ fontSize: 14 }}>
                {locale.reminderToastMessageGiftCard}
              </Text>
            </div>
          </Highlight>
          <div className={styles.spacer10} />
        </>
      )}
      <div className={styles.tableContainer}>
        <DataTableV2
          {...table}
          hideNoSearchResultRender
          tableVersion="v2"
          hidePagination
          size="small"
        />
      </div>
      <div className={styles.spacer50} />
      <PaymentMethodTransactionModal
        method={PaymentMethod.KeyAccount}
        paymentMethodTransactions={keyAccountTransactions}
        transactionsItemDetails={transactionsItemDetails}
        onRefetch={refetchShift}
        pageMode={pageMode}
        updatePaymentMethodRefNumber={handleUpdatePaymentMethodRefNumber}
        updateTransactionPaymentMethod={handleUpdateTransactionPaymentMethod}
        updateTransactionAccountability={updateTransactionAccountability}
        employees={employees}
        {...keyAccountTransactionModal}
      />
    </>
  );
}
